/**
 * Zelvoq panel yardımcıları.
 *
 * Sağlayıcının ayarları `settings` kaydında **değildir** — `provider_configs`
 * satırında tek bir JSON gövdesidir (bkz. backend/services/zelvoq.py). Bunun
 * panel tarafındaki sonucu şudur: alanlar Ayarlar sayfasının ortak `s`
 * state'inde durmaz, kendi state'indedir; dolayısıyla "bu sekmede değişiklik
 * var mı" hesabı da ortak `changedKeys` ile yapılamaz. Karşılaştırma bu yüzden
 * buradadır ve **iki yer birden** okur: kaydedilecek gövde (`changedFields`)
 * ve Kaydet şeridinin görünürlüğü (`dirtyFields`). Ayrışırlarsa yönetici ya
 * kaydedemediği bir değişiklik görür ya da düğmesi hiç çıkmaz.
 */

export const ZELVOQ_DEFAULTS = {
  enabled: false,
  api_url: 'https://ryl.zelvoq.com',
  sid: '',
  secret_key: '',
  secret_with: '',
  deposit_methods: 'banktransfer,card,papara,crypto',
  withdraw_methods: 'banktransfer',
  show_bank: false,
  bank_id: 0,
  deposit_enabled: true,
  withdraw_enabled: true,
  deposit_min_amount: 100,
  deposit_max_amount: 100000,
  withdraw_min_amount: 100,
  withdraw_max_amount: 100000,
  callback_url: '',
  redirect_url: '',
};

export const CALLBACK_PATH = '/api/zelvoq/callback';
// Oyuncunun ödeme sonrası döndüğü sayfa; sitenin kendi kökünden türetilir.
export const REDIRECT_PATH = '/hesap/para-yatir';

/**
 * Sağlayıcının `payment_type` kataloğu — backend'deki `zelvoq.METHODS` ile
 * **aynı kalmalıdır**. Ayrışırsa panelde açılan bir yöntem backend'de sessizce
 * düşer ve yönetici kapattığını sandığı yöntemi vitrinde görmeye devam eder.
 *
 * `withdraw` yalnızca havalede açıktır: sağlayıcı çekimde sayısal bir
 * `bank_id` zorunlu tutar ve kart/kripto/Papara'nın banka listesinde karşılığı
 * yoktur.
 */
export const ZELVOQ_METHODS = [
  { slug: 'banktransfer', label: 'Havale / EFT', deposit: true, withdraw: true },
  { slug: 'card', label: 'Kredi Kartı', deposit: true, withdraw: false },
  { slug: 'papara', label: 'Papara', deposit: true, withdraw: false },
  { slug: 'crypto', label: 'Kripto Para', deposit: true, withdraw: false },
];

const slugList = (value) => String(value ?? '')
  .split(',')
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean);

export const hasMethod = (value, slug) => slugList(value).includes(slug);

/**
 * Yöntemi listeye ekler ya da listeden çıkarır.
 *
 * Sıra katalogdan gelir: yöneticinin işaretleme sırası kayda girmemelidir,
 * yoksa aynı seçim iki farklı dize üretir ve "değişti" sanılır.
 */
export const toggleMethod = (value, slug, checked) => {
  const current = new Set(slugList(value));
  if (checked) current.add(slug);
  else current.delete(slug);
  return ZELVOQ_METHODS
    .map((item) => item.slug)
    .filter((item) => current.has(item))
    .join(',');
};

// Sayı ile metin arasındaki gösterim farkı (panelin `100` ile sunucunun
// `"100.00"`u) değişiklik sayılmamalıdır; boş ve tanımsız da aynı şeydir.
const sameValue = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  if (typeof a === 'number' || typeof b === 'number') {
    const left = Number(a ?? 0);
    const right = Number(b ?? 0);
    if (Number.isFinite(left) && Number.isFinite(right)) return left === right;
  }
  return String(a ?? '') === String(b ?? '');
};

/**
 * Sunucuya gönderilecek gövde: **yalnızca değişen alanlar**.
 *
 * Tam gövde yazmak kayıtlı anahtarı silerdi (uç birleştirme yapar ama
 * maskelenmiş sırrı da geri yazardı). Maskesi olduğu gibi duran secret
 * gönderilmez — panele geri dönmeyen bir değeri geri yazmak onu bozar.
 */
export const changedFields = (current, initial, options = {}) => {
  const { paymentBase = '', maskedSecret = '' } = options;
  const values = current || {};
  const saved = initial || {};
  const payload = {};
  Object.keys(ZELVOQ_DEFAULTS).forEach((key) => {
    const next = values[key];
    if (next === undefined) return;
    if (
      (key === 'secret_key' || key === 'secret_with')
      && (!next || next === maskedSecret)
    ) return;
    if (!sameValue(next, saved[key])) payload[key] = next;
  });
  // Bildirim ve dönüş adresi salt okunurdur ve her zaman sitenin güncel
  // kökünden çizilir; kayıttaki değer geride kalmışsa bu sekme kaydedilirken
  // sessizce düzeltilir (Settings.jsx `CALLBACK_FIELDS` kuralının aynısı).
  // Bu sağlayıcıda adres **istekte gönderildiği** için geride kalmış bir
  // adres, sonucun hiç ulaşmaması demektir.
  if (paymentBase) {
    const callback = `${paymentBase}${CALLBACK_PATH}`;
    if (!sameValue(saved.callback_url, callback)) payload.callback_url = callback;
    const redirect = `${paymentBase}${REDIRECT_PATH}`;
    if (!sameValue(saved.redirect_url, redirect)) payload.redirect_url = redirect;
  }
  return payload;
};

/**
 * Kaydet şeridinin görünürlüğü için değişen alanlar.
 *
 * Adresler **sayılmaz**: onları yönetici yazmaz, kaydederken kendiliğinden
 * güncel köke çekilir. Sayılsaydı sekme hiçbir şeye dokunulmadan "değişti"
 * görünür ve Kaydet düğmesi kalıcı olarak açık kalırdı.
 */
export const dirtyFields = (current, initial, options = {}) => (
  Object.keys(changedFields(current, initial, options))
    .filter((key) => key !== 'callback_url' && key !== 'redirect_url')
);

/**
 * Bağlantı testi yalnızca sunucuda kayıtlı yapılandırmayı okur. Panelde yeni
 * yazılmış bir sid ile önce eski kaydı sınamak, sağlayıcıdan yanıltıcı
 * "sid gerekli" cevabı getirirdi. Kayıt başarısızsa test hiç başlatılmaz.
 */
export const saveBeforeConnectionTest = async (saveConfig, testConnection) => {
  await saveConfig();
  return testConnection();
};

export default {
  ZELVOQ_DEFAULTS,
  ZELVOQ_METHODS,
  CALLBACK_PATH,
  REDIRECT_PATH,
  hasMethod,
  toggleMethod,
  changedFields,
  dirtyFields,
  saveBeforeConnectionTest,
};
